/**
 * Seller discount codes, as the browser sees them.
 *
 * MIRRORS functions/src/lib/discount-codes.ts, which owns every rule that
 * matters: checkout applies a code on the server, and the createDiscountCode,
 * updateDiscountCode and deleteDiscountCode callables are the only writers. This
 * copy lets the dashboard check what a seller types and preview prices, and lets
 * checkout and the cart tell a code from a typo before asking the server.
 * discount-codes.test.ts imports the server module and fails when the two
 * disagree, so change both together.
 *
 * Where a code lives, what it does and why (no stacking with a sale, a fixed
 * amount off each product, never below the price floor, one-off prices only):
 * see the header of the server module.
 *
 * No React and no Firebase, so the type-stripping node test runner can load it.
 */

import { PRICE_MAX_AUD, PRICE_MIN_AUD } from './price-bounds.ts';

/** Most codes one seller may hold at once. */
export const MAX_DISCOUNT_CODES_PER_SELLER = 3;

/** A normalised code: 3 to 20 of A-Z, 0-9 and hyphens, starting and ending with a letter or digit. */
export const DISCOUNT_CODE_PATTERN = /^[A-Z0-9][A-Z0-9-]{1,18}[A-Z0-9]$/;

/** What may be typed, checked BEFORE upper-casing (which maps some non-ASCII letters onto ASCII). */
const TYPED_CODE_CHARS = /^[A-Za-z0-9-]+$/;
const MAX_TYPED_CODE_LENGTH = 64;

export const MIN_PERCENT_OFF = 1;
export const MAX_PERCENT_OFF = 99;
export const MIN_FIXED_OFF_AUD = 0.01;
export const MAX_FIXED_OFF_AUD = PRICE_MAX_AUD;
export const MAX_CODE_PRODUCTS = 500;
export const MAX_CODE_REDEMPTIONS = 1000000;

export type DiscountCodeType = 'percentage' | 'fixed';

/** The code as stored, from whatever was typed: trimmed and upper-cased. Null when it cannot be a code. */
export function normalizeDiscountCode(raw: unknown): string | null {
  if (typeof raw !== 'string' || raw.length > MAX_TYPED_CODE_LENGTH) return null;
  const trimmed = raw.trim();
  if (!TYPED_CODE_CHARS.test(trimmed)) return null;
  const code = trimmed.toUpperCase();
  return DISCOUNT_CODE_PATTERN.test(code) ? code : null;
}

/** Shown under the code field while what is typed cannot be a code. */
export const DISCOUNT_CODE_FORMAT_HINT =
  'Use 3 to 20 letters, numbers or hyphens, starting and ending with a letter or number.';

/**
 * The price a code makes of a regular price, rounded to the cent like a sale
 * price and never below the platform floor. The server's codePriceAud, which
 * is what checkout charges.
 */
export function codePriceAud(regularPriceAud: number, type: DiscountCodeType, value: number): number {
  const raw = type === 'percentage' ? regularPriceAud * (1 - value / 100) : regularPriceAud - value;
  return Math.max(PRICE_MIN_AUD, Math.round(raw * 100) / 100);
}

/** A seller's code, as the dashboard lists it. */
export interface SellerDiscountCode {
  code: string;
  type: DiscountCodeType;
  /** A whole percent for 'percentage'; AUD for 'fixed'. */
  value: number;
  productIds: string[];
  active: boolean;
  expiresAtMs: number | null;
  maxRedemptions: number | null;
  redemptions: number;
  createdAtMs: number;
}

/**
 * A code document from users/{uid}/discountCodes, read defensively. Null for one
 * whose id is not a normalised code or whose type is unknown; the server would
 * refuse to apply those too.
 */
export function parseSellerDiscountCode(id: string, data: unknown): SellerDiscountCode | null {
  const code = normalizeDiscountCode(id);
  if (!code || code !== id || !data || typeof data !== 'object') return null;
  const d = data as Record<string, unknown>;
  if (d.type !== 'percentage' && d.type !== 'fixed') return null;
  const value = typeof d.value === 'number' && Number.isFinite(d.value) ? d.value : NaN;
  if (!Number.isFinite(value)) return null;
  const finiteOr = (v: unknown, fallback: number | null) =>
    typeof v === 'number' && Number.isFinite(v) ? v : fallback;
  const maxRedemptions = finiteOr(d.maxRedemptions, null);
  return {
    code,
    type: d.type,
    value,
    productIds: Array.isArray(d.productIds) ? d.productIds.filter((p): p is string => typeof p === 'string') : [],
    active: d.active === true,
    expiresAtMs: finiteOr(d.expiresAtMs, null),
    maxRedemptions: maxRedemptions !== null && maxRedemptions >= 1 ? Math.floor(maxRedemptions) : null,
    redemptions: Math.max(0, Math.floor(finiteOr(d.redemptions, 0) ?? 0)),
    createdAtMs: finiteOr(d.createdAtMs, 0) ?? 0,
  };
}

export type DiscountCodeStatus = 'active' | 'off' | 'expired' | 'used-up';

/** Whether a code would apply at checkout right now, as far as the code itself goes. */
export function discountCodeStatus(
  code: Pick<SellerDiscountCode, 'active' | 'expiresAtMs' | 'maxRedemptions' | 'redemptions'>,
  nowMs: number,
): DiscountCodeStatus {
  if (!code.active) return 'off';
  if (code.expiresAtMs !== null && nowMs >= code.expiresAtMs) return 'expired';
  if (code.maxRedemptions !== null && code.redemptions >= code.maxRedemptions) return 'used-up';
  return 'active';
}

/** The fields of a product the code rules read. */
export interface CodeProductFields {
  status?: string | null;
  pricingModel?: string | null;
  price?: number | null;
  priceTiers?: unknown[] | null;
}

const isRecurringTier = (tier: unknown) =>
  !!tier && typeof tier === 'object' && (tier as { purchaseType?: unknown }).purchaseType === 'subscription';

/**
 * The one-off prices of a listing, AUD: the price of a standard listing, or
 * the price of each one-off tier of a tiered one. Empty for every other model,
 * which is exactly the set a code cannot touch.
 */
export function oneOffPricesAud(product: CodeProductFields): number[] {
  const model = product.pricingModel;
  if (model === undefined || model === null || model === 'standard') {
    const price = Number(product.price);
    return Number.isFinite(price) && price > 0 ? [Math.round(price * 100) / 100] : [];
  }
  if (model !== 'tiered' || !Array.isArray(product.priceTiers)) return [];
  return product.priceTiers
    .filter((tier) => !!tier && typeof tier === 'object' && !isRecurringTier(tier))
    .map((tier) => Number((tier as { price?: unknown }).price))
    .filter((price) => Number.isFinite(price) && price > 0)
    .map((price) => Math.round(price * 100) / 100);
}

/**
 * Whether the dashboard offers this product for a code: live, and with a
 * one-off price to take something off. The server re-checks ownership and the
 * model whenever a product is added.
 */
export function isCodeEligibleProduct(product: CodeProductFields): boolean {
  if (product.status !== 'live') return false;
  const model = product.pricingModel;
  if (model === undefined || model === null || model === 'standard') return true;
  return model === 'tiered' && oneOffPricesAud(product).length > 0;
}

/** What the amount of a code says, e.g. "20% off" or "A$5.00 off each product". */
export function describeCodeAmount(
  type: DiscountCodeType,
  value: number,
  formatAud: (aud: number) => string,
): string {
  return type === 'percentage' ? `${value}% off` : `${formatAud(value)} off each product`;
}

/** Why the server did not apply a code, as checkout reports it. */
export type DiscountRejection = 'not_applicable' | 'expired' | 'exhausted' | 'no_saving' | 'unavailable';

/**
 * What the buyer is told when a code is not applied. Anything unrecognised
 * (an older server that ignores codes sends none at all) gets the last line.
 */
export function discountRejectionMessage(rejection: unknown): string {
  switch (rejection) {
    case 'not_applicable':
      return "This code doesn't apply to anything in your cart.";
    case 'expired':
      return 'This code has expired.';
    case 'exhausted':
      return 'This code has reached its usage limit.';
    case 'no_saving':
      return 'The items this code covers are already at a lower price.';
    case 'unavailable':
      return "We couldn't check this code right now. Try again.";
    default:
      return "This code can't be used right now.";
  }
}

/**
 * The label under a sale or a receipt line that used a code ("Code SAVE10"),
 * or null. Re-checked like anything read back from a document.
 */
export function discountCodeLabel(stored: unknown): string | null {
  const code = normalizeDiscountCode(stored);
  return code ? `Code ${code}` : null;
}
